import { BreadCrumb } from "primereact/breadcrumb";
import { Card } from "primereact/card";
import type { MenuItem } from "primereact/menuitem";
import Table from "../components/table";
import { TABLE_COLUMNS_CONFIG, type ColumnMeta } from "../constants/table.cnst";
import { useCallback, useState } from "react";
import { Tag } from "primereact/tag";
import { getSeverity } from "../services/util.service";
import { fetchApi } from "../services/http.service";
import { APIMapping } from "../constants/api-mapping.cnst";
import { FILTERS_STORAGE_KEY, HBL_PA_RESOURCES } from "../constants/data.cnst";
import { useQuery } from "@tanstack/react-query";
import type { LocoHealthNew } from "../interfaces/dashboard"
import { MultiSelect } from "primereact/multiselect";
import { DEFAULT_ROUTE_PRIORITY } from "../constants/defaultRoutes";
import { useNavigate } from "react-router-dom";

export default function LocoHealthNew() {

    const breadcrumb: MenuItem[] = [
        { label: 'Health', style: { pointerEvents: 'none', fontWeight: 500 } },
        { label: 'Loco Health', style: { pointerEvents: 'none' }, className: 'font-semibold text-gray-900' }
    ];
    const locoHealthTableColumns: ColumnMeta[] = TABLE_COLUMNS_CONFIG.LOCO_HEALTH_NEW;

    const [locoHealthQueryParams, setLocoHealthQueryParams] = useState({
        total_entry_per_page: 10,
        page_num: 1,
    });
    const navigate = useNavigate();

    const [selectedLocos, setSelectedLocos] = useState([]);
    const [expandAllRows, setExpandAllRows] = useState(true);
    const [expansionRequest, setExpansionRequest] = useState(0);
    const [allRowsExpanded, setAllRowsExpanded] = useState(true);
    const [allRowsCollapsed, setAllRowsCollapsed] = useState(false);
    const handleExpansionStateChange = useCallback((allExpanded: boolean, allCollapsed: boolean) => {
        setAllRowsExpanded(allExpanded);
        setAllRowsCollapsed(allCollapsed);
    }, []);
    const { data: filtersResponseData } = useQuery({
        queryKey: ["locolist-loco-health"],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.GET_LOCOS_IN_LOCO_HEALTH,
                resourceName: HBL_PA_RESOURCES.LOCO_HEALTH
            }),
        enabled: true
    });

    const locoOptions =
        (filtersResponseData?.response ?? []).map((id: any) => ({
            label: id.toString(),
            value: id.toString(),
        }));


    const { data: eventsApiResponse, isFetching } = useQuery<any>({
        queryKey: ["loco-health-table", locoHealthQueryParams, selectedLocos],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.GET_LOCO_HEALTH_NEW,
                queryParams: { ...locoHealthQueryParams, locoId: selectedLocos.join(',') },
                resourceName: HBL_PA_RESOURCES.LOCO_HEALTH
            }),
        enabled: true
    });

    const [first, setFirst] = useState(0);
    const locoHealthTableData: LocoHealthNew[] =
        eventsApiResponse?.response?.locos?.map(
            (rowData: LocoHealthNew) => {
                return {
                    ...rowData,
                    failedHealthModules: rowData.failedModules?.map(
                        (module) =>
                            `${module.category.replaceAll('_', ' ')} ${module.count}`
                    ) ?? [],
                };
            }
        ) ?? [];
    const [rows, setRows] = useState(10);
    const totalEvents = eventsApiResponse?.response?.totalRecords ?? 0;


    const buildFinalPath = (locoData: any, healthModule: any) => {
        const params = new URLSearchParams(location.search);

        params.set("locoId", locoData.locoId);
        params.set("fromDate", locoData['tripStart'].split(",")[0]);
        params.set("toDate", locoData['tripEnd'] ? locoData['tripEnd'].split(",")[0] : locoData['tripStart'].split(",")[0]);

        const path = DEFAULT_ROUTE_PRIORITY.find(r => r.name === healthModule && r.path.includes('loco-health'));

        if (healthModule === "PG Failures") {
            params.delete('locoId')
            const filters = {
                "locos": [
                    locoData.locoId
                ],
                "eventCategories": [
                    "Slip/Slide Detected"
                ],
                "subCategories": [],
                "treeSelection": {
                    "SLIP/SLIDE_DETECTED": {
                        "checked": true,
                        "partialChecked": false
                    }
                }
            }
            localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
            
            navigate({
                pathname: "/dashboard",
                search: `?${params.toString()}`
            });

            return;
        }

        navigate(`${path?.path}?${params.toString()}`);
    };

    const renderLocoHealthExpansion = (
        rowData: LocoHealthNew
    ) => {
        const locoData = rowData;

        return (
            <div className="p-3">

                <Table
                    id="loco-health-expansion"
                    columns={TABLE_COLUMNS_CONFIG.LOCO_HEALTH_EXPANSION}
                    data={rowData.failedModules ?? []}
                    showGridlines={true}
                    paginator={false}
                    first={0}
                    isHighlight={true}
                    rows={rowData.failedModules?.length ?? 0}
                    enableScroll={false}
                    renderCell={(rowData, field) => {

                        if (field === 'failureDetails') {
                            return (
                                <div className="flex flex-wrap gap-2">
                                    {rowData.failureDetails?.map((issue: any) => {

                                        return (
                                            <Tag
                                                key={issue}
                                                value={issue}
                                                severity="info"
                                            />
                                        );
                                    })}
                                </div>
                            );
                        }

                        if (field === 'moduleName') {
                            return (
                                <a id={`row-${field}`} className="underline cursor-pointer text-brand-600 dark:text-brand-50" onClick={() => buildFinalPath(locoData, rowData[field])}>
                                    {rowData[field]}
                                </a>
                            );
                        }

                        return rowData[field];
                    }}
                />

            </div>
        );
    };

    const cardHeader = (

        <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
                <h3 className="m-0 font-semibold">Loco Health</h3>
                <button
                    type="button"
                    aria-label="Expand all rows"
                    title="Expand all rows"
                    disabled={allRowsExpanded}
                    className="p-2 hover:bg-gray-100 rounded cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    onClick={() => {
                        setExpandAllRows(true);
                        setExpansionRequest((request) => request + 1);
                    }}
                >
                    <i className="pi pi-plus-circle" />
                </button>
                <button
                    type="button"
                    aria-label="Collapse all rows"
                    title="Collapse all rows"
                    disabled={allRowsCollapsed}
                    className="p-2 hover:bg-gray-100 rounded cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    onClick={() => {
                        setExpandAllRows(false);
                        setExpansionRequest((request) => request + 1);
                    }}
                >
                    <i className="pi pi-minus-circle" />
                </button>
            </div>

            <MultiSelect
                value={selectedLocos}
                options={locoOptions ?? []} // [{ label, value }]
                filter
                resetFilterOnHide
                onChange={(e: any) => {
                    const selected = e.value;
                    setSelectedLocos(selected);

                    setFirst(0);
                    setRows(10);
                    setLocoHealthQueryParams((prev: any) => ({
                        ...prev,
                        page_num: 1,
                        total_entry_per_page: 10,
                    }));
                }}
                placeholder="Select Locos"
                className="w-80"
                display="chip"
            />
        </div>

    );


    return (
        <div className="h-[calc(100vh-4rem)] p-6 flex-1 overflow-y-auto">

            <BreadCrumb model={breadcrumb} />
            <div className="grid grid-cols-1 mb-6 pt-4">

                <Card header={cardHeader}>

                    <Table
                        id="loco-health"
                        columns={locoHealthTableColumns}
                        data={locoHealthTableData}
                        showGridlines={true}
                        paginator={true}
                        isLoading={isFetching}
                        first={first}
                        rows={rows}
                        totalRecords={totalEvents}
                        renderCell={(rowData, field, rowIndex, path, expansion) => {
                            if (field === 'serial') {
                                return first + rowIndex + 1;
                            }
                            if (field === 'tripStatus') {
                                console.log(path)

                                return (
                                    <>
                                        <Tag
                                            key={rowData.tripStatus}
                                            value={rowData.tripStatus}
                                            severity={getSeverity(rowData.tripStatus)}
                                        />
                                    </>
                                );

                            }
                            if (field === 'failedHealthModules') {
                                return (
                                    <div className="flex flex-wrap gap-2">
                                        {rowData.failedHealthModules?.map((issue: any, index: number) => {
                                            const tagId = `loco-issue-${rowData['locoId']}-${index}`;

                                            return (
                                                <>
                                                    <Tag
                                                        key={issue}
                                                        id={tagId}
                                                        value={issue}
                                                        severity={getSeverity(issue)}
                                                    />

                                                    {/* {issue.issueName !== 'RADIO_HEALTH_DETECTED' &&
                                                        <Tooltip
                                                            target={`#${tagId}`}
                                                            content={
                                                                `Trip Started At: ${issue.tripStartedAt}\n` +
                                                                `Trip Ended At: ${issue.tripEndedAt}\n` +
                                                                `Trip Count: ${issue.tripCount}`
                                                            }
                                                            position="top"
                                                        />
                                                    } */}
                                                </>
                                            );
                                        })}
                                    </div>
                                );
                            }

                            if (field === "action") {
                                return (
                                    <button
                                        type="button"
                                        className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            expansion?.toggle();
                                        }}
                                    >
                                        <i
                                            className={
                                                expansion?.isExpanded
                                                    ? "pi pi-chevron-up"
                                                    : "pi pi-chevron-down"
                                            }
                                        />
                                    </button>
                                );
                            }

                            return rowData[field];
                        }}
                        rowExpansion={{
                            key: 'locoId',
                            render: renderLocoHealthExpansion
                        }}
                        expandAllRows={expandAllRows}
                        expansionRequest={expansionRequest}
                        onExpansionStateChange={handleExpansionStateChange}
                        onPageChangeExternal={(page_num, total_entry_per_page) => {
                            setFirst((page_num - 1) * total_entry_per_page);
                            setRows(total_entry_per_page);
                            setLocoHealthQueryParams((prev: any) => ({
                                ...prev,
                                page_num: page_num,
                                total_entry_per_page: total_entry_per_page
                            }));


                        }}
                    />
                </Card>
            </div>
        </div>
    );

}